/* eslint-disable camelcase */
import { createColumnHelper, flexRender, getCoreRowModel, Table, useReactTable } from "@tanstack/react-table";
import classNames from "classnames";
import omit from "lodash/omit";
import { getAPIFilters, getValueFilters } from "PFApp/use_filtered_collection";
import Checkbox from "PFComponents/checkbox/checkbox";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { Typography } from "PFComponents/typography";
import { stringify } from "PFCore/helpers/use_deterministic_stringity";
import useStorage from "PFCore/helpers/use_storage";
import { useBookingConfigCalendars } from "PFCore/hooks/queries/bookings/booking_config/use_booking_config_calendars";
import { useBookingConfigProfileCalendars } from "PFCore/hooks/queries/bookings/booking_config/use_booking_config_profile_calendars";
import { useRemoveBookingConfigProfileCalendar } from "PFCore/hooks/queries/bookings/booking_config/use_remove_booking_config_profile_calendar";
import { Filters as FiltersType, Value } from "PFTypes";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { BookingConfigSearch } from "../../../booking_config_search";
import bookingCalendarCss from "../../booking_calendars.module.scss";
import { Filters } from "../filters";
import {
  CalendarNameCell,
  ErrorsCell,
  ErrorsHeader,
  StartDatesCell,
  Toolbar,
  UnassignCalendarsCell,
  UserCell,
  WorkforceTableLoader
} from "./components";
import { ProfileRowErrors, WorkforceRowData } from "./types";
import css from "./workforce.module.scss";

enum Columns {
  Select = "SELECT_ROW",
  User = "USER_NAME",
  Calendars = "CALENDARS_NAMES",
  StartDates = "START_DATES",
  Delete = "DELETE_CALENDAR",
  Errors = "ERRORs"
}

const columnHelper = createColumnHelper<WorkforceRowData>();
const INITIAL_PAGE = 1;

const Workforce = () => {
  const { t } = useTranslation("bookingModule");
  const [profileRowsErrors, setProfileRowsErrors] = useState<ProfileRowErrors>({});
  const [filters, setFilters] = useStorage<FiltersType<Value>>("adminBookingProfileCalendars", {});
  const [page, setPage] = useState(INITIAL_PAGE);

  const { data } = useBookingConfigCalendars();
  const calendars = data?.entries || [];
  const {
    data: profileCalendars,
    isLoading,
    isFetching
  } = useBookingConfigProfileCalendars(null, getAPIFilters(filters), page);
  const { mutateAsync: removeCalendar } = useRemoveBookingConfigProfileCalendar();

  useEffect(() => {
    setFilters(getValueFilters(profileCalendars?.meta?.filters));
  }, [stringify(getValueFilters(profileCalendars?.meta?.filters))]);

  useEffect(() => {
    setPage(INITIAL_PAGE);
  }, [filters]);

  const columns = [
    {
      id: Columns.Select,
      cell: ({ row }) => (
        <Checkbox checked={row.getIsSelected()} handleChange={row.getToggleSelectedHandler()} />
      )
    },
    columnHelper.accessor("profile", {
      id: Columns.User,
      header: t("config.calendars.workforce.name"),
      cell: (info) => <UserCell profile={info.getValue()} />
    }),
    columnHelper.accessor("profile_calendars", {
      id: Columns.Calendars,
      header: t("config.calendars.workforce.calendar"),
      cell: ({ getValue: getDates }) => <CalendarNameCell startDates={getDates()} calendars={calendars} />
    }),
    columnHelper.accessor("profile_calendars", {
      id: Columns.StartDates,
      header: t("config.calendars.workforce.start"),
      cell: ({ getValue: getDates }) => <StartDatesCell startDates={getDates()} />
    }),
    columnHelper.accessor((row) => row, {
      id: Columns.Delete,
      header: t("config.calendars.workforce.delete"),
      cell: (info) => {
        const { profile_calendars, profile } = info.getValue();

        return (
          <UnassignCalendarsCell
            profileCalendars={profile_calendars}
            profile={profile}
            onUnassign={handleUnassign}
          />
        );
      }
    }),
    columnHelper.accessor((row) => row, {
      id: Columns.Errors,
      header: () => <ErrorsHeader errors={profileRowsErrors} />,
      cell: ({ row }) => <ErrorsCell errors={profileRowsErrors} profileId={row.original.profile.id} />
    })
  ];

  const tableInstance: Table<WorkforceRowData> = useReactTable({
    columns,
    data: profileCalendars?.entries || [],
    getRowId: (row) => String(row.profile.id),
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel()
  });

  useEffect(() => {
    tableInstance.resetRowSelection();
  }, [page]);

  const setProfileRowError = useCallback(
    (profileId, jqueryXHR) => {
      const error = jqueryXHR?.responseJSON.errors?.[0]?.detail;
      setProfileRowsErrors((prev) => ({ ...prev, [profileId]: error }));
    },
    [setProfileRowsErrors]
  );
  const cleanProfileRowErrors = useCallback(
    (profileId) => setProfileRowsErrors((prev) => ({ ...omit(prev, [profileId]) })),
    [setProfileRowsErrors]
  );

  const handleUnassign = ({ profileId, profileCalendar }) => {
    removeCalendar({ recordId: profileCalendar.id, profileId }).then(
      () => cleanProfileRowErrors(profileId),
      (event) => setProfileRowError(profileId, event)
    );
  };

  const handleTermChange = (term) => {
    setFilters((state: FiltersType<Value>) => ({
      ...state,
      fields: { ...state.fields, term }
    }));
  };

  return (
    <div className={classNames(css.workforcePanel, { [css.workforceLoading]: isFetching || isLoading })}>
      <Filters meta={profileCalendars?.meta} onFiltersChange={setFilters} isLoading={isLoading} />
      <WorkforceTableLoader isLoading={isLoading}>
        <BookingConfigSearch
          onTermChange={handleTermChange}
          term={(filters?.fields?.term as unknown as string) || ""}
        />
        <Toolbar
          tableInstance={tableInstance}
          setProfileRowError={setProfileRowError}
          cleanProfileRowErrors={cleanProfileRowErrors}
          profileCalendars={profileCalendars}
        />
        {tableInstance.getHeaderGroups().map((headerGroup) => (
          <div key={headerGroup.id} className={classNames(bookingCalendarCss.headers, css.workforceGrid)}>
            {headerGroup.headers.map((header) => (
              <span key={header.id}>
                {header.isPlaceholder ? null : (
                  <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                )}
              </span>
            ))}
          </div>
        ))}
        {tableInstance.getRowModel().rows.map((row) => (
          <div key={row.id} className={classNames(css.workforceGrid, bookingCalendarCss.calendar)}>
            {row.getVisibleCells().map((cell) => flexRender(cell.column.columnDef.cell, cell.getContext()))}
          </div>
        ))}
        {tableInstance.getRowModel().rows.length === 0 && (
          <Typography variant="h4" className={css.notFound}>
            {t("config.noUsersFound")}
          </Typography>
        )}
        <SimplePagination
          currentPage={profileCalendars?.meta?.page}
          handleClick={setPage}
          totalPages={profileCalendars?.meta?.totalPages}
        />
      </WorkforceTableLoader>
    </div>
  );
};

export default Workforce;
